/**
 * Merchant autotagging — matches raw Plaid transaction descriptions to
 * well-known merchants and returns a canonical name + category (with a colour
 * and lucide-react icon name) for the UI to render a subtle badge.
 *
 * Pattern order matters: the first match wins. Keep the more specific patterns
 * (e.g. "amzn mktp" for Amazon marketplace) above the generic ones ("amazon").
 */

// Category palette — kept in one place so we can tune all badges at once.
export const CATEGORIES = {
  shopping:   { color: "#F59E0B", label: "Shopping",   icon: "ShoppingBag" },
  groceries:  { color: "#10B981", label: "Groceries",  icon: "ShoppingCart" },
  food:       { color: "#F97316", label: "Food & Drink", icon: "Coffee" },
  transport:  { color: "#3B82F6", label: "Transport",  icon: "Bus" },
  streaming:  { color: "#A78BFA", label: "Streaming",  icon: "Play" },
  utilities:  { color: "#38BDF8", label: "Bills",      icon: "Zap" },
  fitness:    { color: "#14B8A6", label: "Fitness",    icon: "Dumbbell" },
  travel:     { color: "#EC4899", label: "Travel",     icon: "Plane" },
  finance:    { color: "#94A3B8", label: "Finance",    icon: "Landmark" },
};

// [regex, merchant_name, category] — regex applied against lowercased description
const RULES = [
  // Shopping
  [/\b(amzn|amazon)\b/, "Amazon", "shopping"],
  [/\bebay\b/, "eBay", "shopping"],
  [/\basos\b/, "ASOS", "shopping"],
  [/\bargos?\b/, "Argos", "shopping"],
  [/\bjohn lewis\b/, "John Lewis", "shopping"],
  [/\bmarks (?:&|and) spencer|m&s|\bmarks & spencer\b/, "Marks & Spencer", "shopping"],
  [/\bikea\b/, "IKEA", "shopping"],
  [/\bapple\.com|apple store\b/, "Apple", "shopping"],

  // Groceries
  [/\btesco\b/, "Tesco", "groceries"],
  [/\bsainsbury/, "Sainsbury's", "groceries"],
  [/\bwaitrose\b/, "Waitrose", "groceries"],
  [/\bmorrisons?\b/, "Morrisons", "groceries"],
  [/\baldi\b/, "Aldi", "groceries"],
  [/\blidl\b/, "Lidl", "groceries"],
  [/\bco[\s-]?op\b/, "Co-op", "groceries"],
  [/\basda\b/, "Asda", "groceries"],
  [/\biceland\b/, "Iceland", "groceries"],

  // Food & drink
  [/\bstarbucks\b/, "Starbucks", "food"],
  [/\bcosta\b/, "Costa Coffee", "food"],
  [/\bpret\b/, "Pret", "food"],
  [/\bmcdonald|mcds\b/, "McDonald's", "food"],
  [/\bkfc\b/, "KFC", "food"],
  [/\bnandos?\b/, "Nando's", "food"],
  [/\bgreggs\b/, "Greggs", "food"],
  [/\bdominos?\b/, "Domino's", "food"],
  [/\bpizza hut\b/, "Pizza Hut", "food"],
  [/\bwagamama\b/, "Wagamama", "food"],
  [/\bdeliveroo\b/, "Deliveroo", "food"],
  [/\buber ?eats\b/, "Uber Eats", "food"],
  [/\bjust ?eat\b/, "Just Eat", "food"],

  // Transport
  [/\buber(?! ?eats)\b/, "Uber", "transport"],
  [/\bbolt\.eu|bolt taxi|\bbolt\b/, "Bolt", "transport"],
  [/\btfl\b|transport for london/, "Transport for London", "transport"],
  [/\bnational rail|trainline\b/, "Trainline", "transport"],
  [/\blner\b|london north eastern/, "LNER", "transport"],
  [/\bshell\b/, "Shell", "transport"],
  [/\bbp\b/, "BP", "transport"],
  [/\besso\b/, "Esso", "transport"],

  // Streaming & digital
  [/\bnetflix\b/, "Netflix", "streaming"],
  [/\bspotify\b/, "Spotify", "streaming"],
  [/\bdisney\+?\b/, "Disney+", "streaming"],
  [/\bhbo|now tv\b/, "Sky / NOW", "streaming"],
  [/\byoutube (?:premium|music)\b/, "YouTube", "streaming"],
  [/\bapple music|itunes|apple tv\b/, "Apple Music", "streaming"],
  [/\bamazon prime\b/, "Prime Video", "streaming"],
  [/\bsteam(?:powered)?\b|valve/, "Steam", "streaming"],

  // Utilities / bills
  [/\bbritish gas\b/, "British Gas", "utilities"],
  [/\bocto?pus energy\b/, "Octopus Energy", "utilities"],
  [/\be\.?on\b/, "E.ON", "utilities"],
  [/\bthames water|anglian water|severn trent\b/, "Water bill", "utilities"],
  [/\bvodafone\b/, "Vodafone", "utilities"],
  [/\bee mobile|\bee\b/, "EE", "utilities"],
  [/\bo2\b/, "O2", "utilities"],
  [/\bthree\b|hutchison/, "Three", "utilities"],
  [/\bsky\b(?! ?bet)/, "Sky", "utilities"],
  [/\bvirgin media\b/, "Virgin Media", "utilities"],
  [/\bbt\.com|bt group\b|british telecom/, "BT", "utilities"],
  [/\bcouncil tax\b/, "Council Tax", "utilities"],

  // Fitness
  [/\bpuregym\b/, "PureGym", "fitness"],
  [/\bnuffield\b/, "Nuffield Health", "fitness"],
  [/\bthe gym group|gymgroup\b/, "The Gym Group", "fitness"],
  [/\bfitness first\b/, "Fitness First", "fitness"],

  // Travel
  [/\bryanair\b/, "Ryanair", "travel"],
  [/\beasyjet\b/, "easyJet", "travel"],
  [/\bbritish airways|\bba \b/, "British Airways", "travel"],
  [/\bairbnb\b/, "Airbnb", "travel"],
  [/\bbooking\.com\b/, "Booking.com", "travel"],
  [/\bexpedia\b/, "Expedia", "travel"],
  [/\bhotels\.com\b/, "Hotels.com", "travel"],

  // Finance
  [/\bpaypal\b/, "PayPal", "finance"],
  [/\brevolut\b/, "Revolut", "finance"],
  [/\bmonzo\b/, "Monzo", "finance"],
  [/\bstarling\b/, "Starling", "finance"],
  [/\bwise\b|transferwise/, "Wise", "finance"],
];

const CACHE = new Map();

export const detectMerchant = (description) => {
  if (!description || typeof description !== "string") return null;
  if (CACHE.has(description)) return CACHE.get(description);
  const hay = description.toLowerCase();
  for (const [rx, name, category] of RULES) {
    if (rx.test(hay)) {
      const result = { name, category, ...CATEGORIES[category] };
      CACHE.set(description, result);
      return result;
    }
  }
  CACHE.set(description, null);
  return null;
};

/**
 * Given an array of transactions, return a Map<merchant_key, count> so the UI
 * can show a subtle `× 3 this month` counter next to the merchant name.
 */
export const countMerchants = (items) => {
  const counts = new Map();
  for (const it of items) {
    const m = detectMerchant(it.description);
    if (!m) continue;
    counts.set(m.name, (counts.get(m.name) || 0) + 1);
  }
  return counts;
};
